import * as React from 'react'
import { KeyRound, Plus } from 'lucide-react'
import type { ApiKeyDto } from '@lib/dto/tenant-settings.dto'
import { ApiKeyTable } from '@/components/keys/ApiKeyTable'
import { CreateKeyDialog } from '@/components/keys/CreateKeyDialog'
import { RevokeKeyDialog } from '@/components/keys/RevokeKeyDialog'
import { PageHeader } from '@/components/PageHeader'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { Button } from '@/components/ui/button'
import { canManageKeys, useDashboardScope } from '@/dashboard/DashboardScope'
import { ReadOnlyBanner } from '@/dashboard/ReadOnlyBanner'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useApiKeys, useSettingsActions } from '@/hooks/useTenantSettings'

/**
 * The organization's API keys in the mode on screen. Everyone sees the list
 * (never the secrets); owners create and revoke live keys, owners and
 * developers test keys. A new key's secret is shown once.
 */
export function ApiKeysPage() {
  const scope = useDashboardScope()
  const query = useApiKeys()
  const actions = useSettingsActions()
  const [creating, setCreating] = React.useState(false)
  const [revoking, setRevoking] = React.useState<ApiKeyDto | null>(null)
  const canManage = canManageKeys(scope)
  const keys = (query.data?.keys ?? []).filter((key) => key.mode === scope.mode)

  const createButton = canManage ? (
    <Button onClick={() => setCreating(true)}>
      <Plus />
      Create {scope.mode} key
    </Button>
  ) : null

  function emptyDescription(): string {
    if (scope.mode === 'test') {
      return canManage
        ? 'Test keys create test envelopes: same API, separate data, nothing legally binding.'
        : 'Owners and developers create test keys.'
    }
    return canManage ? 'Create a key for each service that calls PixelSign.' : 'An owner of this organization creates live keys.'
  }

  function renderBody() {
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) {
      return <ErrorState message={apiErrorMessage(query.error, 'Could not load API keys.')} />
    }
    if (keys.length === 0) {
      return (
        <EmptyState
          icon={KeyRound}
          title={`No ${scope.mode} keys yet`}
          description={emptyDescription()}
          {...(createButton ? { action: createButton } : {})}
        />
      )
    }
    return (
      <ApiKeyTable keys={keys} canRevoke={() => canManage} onRevoke={setRevoking} />
    )
  }

  return (
    <>
      <PageHeader
        title="API keys"
        description="Keys your servers use to call the PixelSign API. Send one as a Bearer token."
        actions={createButton}
      />
      <ReadOnlyBanner />
      {renderBody()}
      {canManage ? (
        <>
          <CreateKeyDialog
            mode={scope.mode}
            open={creating}
            onOpenChange={setCreating}
            onCreate={(input) => actions.createKey.mutateAsync(input)}
          />
          <RevokeKeyDialog
            apiKey={revoking}
            onClose={() => setRevoking(null)}
            onRevoke={(keyId) => actions.revokeKey.mutateAsync(keyId)}
          />
        </>
      ) : null}
    </>
  )
}
